import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver, type Driver, type DriveStep } from 'driver.js';
import type { PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tutorialOverrides.css';
import { useTutorialStore } from './useTutorialStore';
import { getStepsForTutorial } from './tutorialSteps';
import type { TutorialStepConfig } from './tutorialSteps';
import { useNetworkChartStore } from '@/stores/useNetworkChartStore';

/** z-index: driver overlay is 10000, popover is 1e9. We sit between to block the cutout. */
const TUTORIAL_BLOCKING_LAYER_Z = 10001;

/** Strip id and route from step config for driver.js (driver only needs element + popover). */
function toDriveStep(step: TutorialStepConfig): DriveStep {
  const { id: _id, route: _route, ...rest } = step;
  return rest;
}

/** Return first focusable button in popover (close, previous, next). */
function getFocusablePopoverButtons(popover: PopoverDOM): HTMLButtonElement[] {
  return [popover.closeButton, popover.previousButton, popover.nextButton].filter(
    (el): el is HTMLButtonElement => el != null && !el.disabled && el.offsetParent != null
  );
}

export const TutorialTour: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const driverRef = useRef<Driver | null>(null);
  const focusTrapCleanupRef = useRef<(() => void) | null>(null);
  const {
    isRunning,
    currentTutorialId,
    stepIndex,
    setStepIndex,
    endTutorial,
  } = useTutorialStore();

  const steps = currentTutorialId ? getStepsForTutorial(currentTutorialId) : [];
  const driverSteps: DriveStep[] = steps.map(toDriveStep);

  useEffect(() => {
    if (!isRunning || !currentTutorialId || stepIndex < 0 || stepIndex >= steps.length) return;

    const step = steps[stepIndex];
    const stepRoute = step?.route ?? '/dashboard';

    if (location.pathname !== stepRoute) {
      navigate(stepRoute);
      return;
    }

    const delayMs = currentTutorialId === 'node-graph' ? 400 : 100;
    /** Step 9 uses vis `focus()` + bounding-box frame; give the graph time to settle before anchoring the popover. */
    const NODE_GRAPH_CONTEXT_SEARCH_SETTLE_MS = 600;
    let cancelled = false;

    const runDriver = () => {
      if (!driverRef.current) {
        driverRef.current = driver({
          showProgress: true,
          steps: driverSteps,
          nextBtnText: 'Next',
          prevBtnText: 'Previous',
          doneBtnText: 'Done',
          allowClose: true,
          disableActiveInteraction: true,
          overlayClickBehavior: () => {},
          onPopoverRender: (popover) => {
            const focusables = getFocusablePopoverButtons(popover);
            const first = focusables[0];
            if (first) first.focus();
            const handler = (e: FocusEvent) => {
              if (popover.wrapper && !popover.wrapper.contains(e.target as Node)) {
                setTimeout(() => focusables[0]?.focus(), 0);
              }
            };
            document.addEventListener('focusin', handler, true);
            focusTrapCleanupRef.current = () => {
              document.removeEventListener('focusin', handler, true);
              focusTrapCleanupRef.current = null;
            };
          },
          onDestroyed: () => {
            focusTrapCleanupRef.current?.();
            driverRef.current = null;
          },
          onNextClick: (_element, _step, opts) => {
            const idx = opts.state.activeIndex ?? stepIndex;
            if (currentTutorialId === 'node-graph' && steps[idx]?.id === 'node-graph-modal-create') {
              const product = useTutorialStore.getState().nodeGraphTutorialModalProduct;
              const create = useNetworkChartStore.getState().onCreateNodeGraph;
              if (product && create) {
                create(product.id, product);
              }
            }
            const nextIdx = idx + 1;
            if (nextIdx >= steps.length) {
              opts.driver.destroy();
              endTutorial();
              if (currentTutorialId === 'primary') {
                navigate('/dashboard');
              }
              return;
            }
            const nextStepConfig = steps[nextIdx];
            const nextRoute = nextStepConfig?.route ?? '/dashboard';
            opts.driver.destroy();
            setStepIndex(nextIdx);
            if (nextRoute !== location.pathname) {
              navigate(nextRoute);
            }
          },
          onPrevClick: (_element, _step, opts) => {
            const idx = opts.state.activeIndex ?? stepIndex;
            const prevIdx = idx - 1;
            if (prevIdx < 0) return;
            opts.driver.destroy();
            setStepIndex(prevIdx);
            const prevStepConfig = steps[prevIdx];
            const prevRoute = prevStepConfig?.route ?? '/dashboard';
            if (prevRoute !== location.pathname) {
              navigate(prevRoute);
            }
          },
          onCloseClick: (_el, _s, closeOpts) => {
            closeOpts.driver.destroy();
            endTutorial();
            if (currentTutorialId === 'primary') {
              navigate('/dashboard');
            }
          },
        });
      }
      driverRef.current.drive(stepIndex);
    };

    const afterDelay = async () => {
      await new Promise((r) => setTimeout(r, delayMs));
      if (cancelled) return;
      const stepElement = steps[stepIndex]?.element;
      const selector = typeof stepElement === 'string' ? stepElement : null;
      if (currentTutorialId === 'node-graph' && selector) {
        const deadline = Date.now() + 12000;
        while (!cancelled && !document.querySelector(selector) && Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 150));
        }
      }
      if (cancelled) return;
      const stepId = steps[stepIndex]?.id;
      if (
        currentTutorialId === 'node-graph' &&
        stepId === 'node-graph-context-search'
      ) {
        await new Promise((r) => setTimeout(r, NODE_GRAPH_CONTEXT_SEARCH_SETTLE_MS));
      }
      if (cancelled) return;
      runDriver();
    };

    afterDelay();
    return () => {
      cancelled = true;
      focusTrapCleanupRef.current?.();
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, [isRunning, currentTutorialId, stepIndex, location.pathname, navigate, endTutorial, setStepIndex, steps.length]);

  // When tour ends, destroy driver
  useEffect(() => {
    if (!isRunning && driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
  }, [isRunning]);

  // Full-screen layer above the driver overlay so the cutout (highlight window) is not
  // clickable — clicks would otherwise fall through the SVG hole to the page. Popover
  // stays above this layer (z-index 1e9) and remains the only interactive surface.
  if (!isRunning || typeof document === 'undefined') return null;
  const blockingLayer = (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: TUTORIAL_BLOCKING_LAYER_Z,
        pointerEvents: 'auto',
        background: 'transparent',
      }}
    />
  );
  return createPortal(blockingLayer, document.body);
}
